/** Correlates add-participation flow lifecycle events with outcome events in Amplitude. */
export const PDR_ADD_PARTICIPATION_FLOW_KEY = "add_participation";

/** Staff journey through an add-participation UI (start, progress, future conclusion). */
export const PDR_FLOW_ANALYTICS_EVENTS = {
  addParticipationStarted: "pdr add participation flow started",
  addParticipationProgressed: "pdr add participation flow progressed"
} as const;

/** Review-step affordances on bulk add-participants (staged import review page). */
export type AddParticipationReviewProgressStep =
  | "review_opened"
  | "validation_filter_applied"
  | "export_csv"
  | "inline_person_corrected"
  | "inline_field_edited"
  | "bulk_remove_confirmed"
  | "bulk_edit_confirmed"
  | "finalize_opened"
  | "add_more_opened"
  | "session_discarded";

export type AddParticipationValidationFilter = "all" | "valid" | "invalid";

export type AddParticipationInlineField = "attendanceType" | "participationRole" | "quantity";

/** Discrete point-in-time outcomes and views — not flow lifecycle events. */
export const PDR_OUTCOME_ANALYTICS_EVENTS = {
  participationCreated: "pdr participation created",
  typeLevelParticipationCreated: "pdr type level participation created",
  legacyPdrCreditCreated: "pdr legacy credit created",
  cycleViewed: "pdr cycle viewed",
  cycleHistoryFiltered: "pdr cycle history filtered"
} as const;

export const PDR_ANALYTICS_EVENTS = {
  ...PDR_FLOW_ANALYTICS_EVENTS,
  ...PDR_OUTCOME_ANALYTICS_EVENTS
} as const;

export type PdrParticipationEntryPoint =
  | "activity_participants"
  | "add_participants_review"
  | "person_professional_development";

export type PdrTypeLevelEntryPoint = "activity_type_participants" | "person_professional_development";

export type PdrParticipationMode = "single" | "bulk";

export type PdrBulkInputMethod = "csv" | "paste";

export type PdrParticipationKind = "activity-level" | "type-level" | "ccms-legacy";

type StoredParticipationFlowContext = {
  startedAtMs: number;
  entryPoint: PdrParticipationEntryPoint;
  bulkInputMethod?: PdrBulkInputMethod;
};

const FLOW_STORAGE_PREFIX = "m360_pdr_participation_flow";

export function pdrParticipationFlowStorageKey(activityId: string) {
  return `${FLOW_STORAGE_PREFIX}_${activityId}`;
}

export function storePdrParticipationFlowContext(activityId: string, context: StoredParticipationFlowContext) {
  try {
    sessionStorage.setItem(pdrParticipationFlowStorageKey(activityId), JSON.stringify(context));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readAndClearPdrParticipationFlowContext(activityId: string): StoredParticipationFlowContext | null {
  const key = pdrParticipationFlowStorageKey(activityId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    return JSON.parse(raw) as StoredParticipationFlowContext;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

export function getBulkInputMethod(hasCsvFile: boolean): PdrBulkInputMethod {
  return hasCsvFile ? "csv" : "paste";
}

export function getParticipationFlowDurationSeconds(startedAtMs: number) {
  return Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
}

type ParticipationCreatedAnalyticsInput = {
  source: PdrParticipationEntryPoint;
  mode: PdrParticipationMode;
  participationKind: PdrParticipationKind;
  success?: boolean;
  activityId?: string;
  participantCount?: number;
  bulkInputMethod?: PdrBulkInputMethod;
  durationSeconds?: number;
};

export function getParticipationCreatedAnalyticsData({
  source,
  mode,
  participationKind,
  success = true,
  activityId,
  participantCount,
  bulkInputMethod,
  durationSeconds
}: ParticipationCreatedAnalyticsInput) {
  return {
    flowKey: PDR_ADD_PARTICIPATION_FLOW_KEY,
    source,
    mode,
    participationKind,
    success,
    ...(activityId ? { activityId } : {}),
    ...(participantCount !== undefined ? { participantCount } : {}),
    ...(bulkInputMethod ? { bulkInputMethod } : {}),
    ...(durationSeconds !== undefined ? { durationSeconds } : {})
  };
}

type AddParticipationFlowStartedAnalyticsInput = {
  source: PdrParticipationEntryPoint | PdrTypeLevelEntryPoint;
  activityId?: string;
  participationKind?: PdrParticipationKind;
};

export function getAddParticipationFlowStartedAnalyticsData({
  source,
  activityId,
  participationKind
}: AddParticipationFlowStartedAnalyticsInput) {
  return {
    flowKey: PDR_ADD_PARTICIPATION_FLOW_KEY,
    source,
    ...(activityId ? { activityId } : {}),
    ...(participationKind ? { participationKind } : {})
  };
}

type AddParticipationFlowProgressedAnalyticsInput = {
  source: PdrParticipationEntryPoint;
  step: AddParticipationReviewProgressStep;
  activityId?: string;
  participantCount?: number;
  validationFilter?: AddParticipationValidationFilter;
  hasSearchTerm?: boolean;
  inlineField?: AddParticipationInlineField;
  stagedParticipantCount?: number;
  invalidParticipantCount?: number;
  bulkEditFields?: string[];
};

export function getAddParticipationFlowProgressedAnalyticsData({
  source,
  step,
  activityId,
  participantCount,
  validationFilter,
  hasSearchTerm,
  inlineField,
  stagedParticipantCount,
  invalidParticipantCount,
  bulkEditFields
}: AddParticipationFlowProgressedAnalyticsInput) {
  return {
    flowKey: PDR_ADD_PARTICIPATION_FLOW_KEY,
    source,
    step,
    ...(activityId ? { activityId } : {}),
    ...(participantCount !== undefined ? { participantCount } : {}),
    ...(validationFilter ? { validationFilter } : {}),
    ...(hasSearchTerm !== undefined ? { hasSearchTerm } : {}),
    ...(inlineField ? { inlineField } : {}),
    ...(stagedParticipantCount !== undefined ? { stagedParticipantCount } : {}),
    ...(invalidParticipantCount !== undefined ? { invalidParticipantCount } : {}),
    ...(bulkEditFields?.length ? { bulkEditFields } : {})
  };
}

type TypeLevelParticipationAnalyticsInput = {
  source: PdrTypeLevelEntryPoint;
  activityTypeId: string;
  success?: boolean;
  durationSeconds?: number;
};

export function getTypeLevelParticipationAnalyticsData({
  source,
  activityTypeId,
  success = true,
  durationSeconds
}: TypeLevelParticipationAnalyticsInput) {
  return {
    flowKey: PDR_ADD_PARTICIPATION_FLOW_KEY,
    source,
    activityTypeId,
    success,
    ...(durationSeconds !== undefined ? { durationSeconds } : {})
  };
}

type LegacyPdrCreditAnalyticsInput = {
  success?: boolean;
  durationSeconds?: number;
  activityCode?: string | null;
};

export function getLegacyPdrCreditAnalyticsData({
  success = true,
  durationSeconds,
  activityCode
}: LegacyPdrCreditAnalyticsInput) {
  return {
    flowKey: PDR_ADD_PARTICIPATION_FLOW_KEY,
    success,
    ...(durationSeconds !== undefined ? { durationSeconds } : {}),
    ...(activityCode ? { activityCode } : {})
  };
}

type CycleViewedAnalyticsInput = {
  viewingCycleCode: string;
  memberCurrentCycleCode?: string;
  isNonCurrentMemberCycle: boolean;
};

export function getCycleViewedAnalyticsData({
  viewingCycleCode,
  memberCurrentCycleCode,
  isNonCurrentMemberCycle
}: CycleViewedAnalyticsInput) {
  return {
    viewingCycleCode,
    ...(memberCurrentCycleCode ? { memberCurrentCycleCode } : {}),
    isNonCurrentMemberCycle
  };
}

type CycleHistoryFilteredAnalyticsInput = {
  historyScope: "selected_cycle" | "all_cycles";
  viewingCycleCode: string;
  hasActivityDateRange?: boolean;
};

export function getCycleHistoryFilteredAnalyticsData({
  historyScope,
  viewingCycleCode,
  hasActivityDateRange
}: CycleHistoryFilteredAnalyticsInput) {
  return {
    historyScope,
    viewingCycleCode,
    ...(hasActivityDateRange !== undefined ? { hasActivityDateRange } : {})
  };
}
